import Head from 'next/head';
import { NextPage } from 'next';
import getConfig from 'next/config';

import {
  ActionCenterDetails,
  ContactInfo,
  MatchedTargets,
} from '@/core/interfaces/common';
import { wrapper } from '@/core/store/store';

import { TakeActionLandingPageV1 } from '@/features/TakeActionLandingPageV1';
import { TakeActionLandingPageV2 } from '@/features/TakeActionLandingPageV2';
import { TakeActionLandingPageV3 } from '@/features/TakeActionLandingPageV3';
import { TakeActionLandingPageV4 } from '@/features/TakeActionLandingPageV4';

import { request } from '@/utils/request';
import { fetchActionCenterData } from '@/utils/requestHelpers';
import { viewerIpHeaders } from '@/utils/viewerIp';

import ErrorPage from '../404';

interface LandingPageProps {
  actionCenterDetails?: ActionCenterDetails;
  contactInfo: ContactInfo;
  nonGeocodedTargets: MatchedTargets;
}

interface RedirectResponse {
  url: string;
}

const { REACT_APP_LANDING_VERSION } = getConfig().publicRuntimeConfig;

const LandingPage: NextPage<LandingPageProps> = (initialProps: LandingPageProps) => {
  const {
    actionCenterDetails,
    contactInfo,
    nonGeocodedTargets,
  } = initialProps;

  if (!actionCenterDetails) {
    return (
      <>
        <Head>
          <meta
            name="robots"
            content="noindex"
          />
        </Head>
        <ErrorPage />
      </>
    );
  }

  const title = `${actionCenterDetails.headlineConfig.headlineText || 'Take Action'} | ${actionCenterDetails.organization.name}`;

  const renderLandingPage = (version: string) => {
    switch (version) {
      case 'version1':
        return (
          <TakeActionLandingPageV1
            actionCenterDetails={actionCenterDetails}
            contactInfo={contactInfo}
            nonGeocodedTargets={nonGeocodedTargets}
          />
        );
      case 'version2':
        return (
          <TakeActionLandingPageV2
            actionCenterDetails={actionCenterDetails}
            contactInfo={contactInfo}
            nonGeocodedTargets={nonGeocodedTargets}
          />
        );
      case 'version3':
        return (
          <TakeActionLandingPageV3
            actionCenterDetails={actionCenterDetails}
            contactInfo={contactInfo}
            nonGeocodedTargets={nonGeocodedTargets}
          />
        );
      case 'version4':
        return (
          <TakeActionLandingPageV4
            actionCenterDetails={actionCenterDetails}
            contactInfo={contactInfo}
            nonGeocodedTargets={nonGeocodedTargets}
          />
        );
      default:
        return '';
    }
  };

  return (
    <>
      <Head>
        <title key="title">{title}</title>
        <meta
          property="og:title"
          content={title}
        />
        <meta
          property="og:image"
          content={actionCenterDetails.landingPageImage}
        />
        {actionCenterDetails.headlineConfig.secondHeadlineText && (
          <>
            <meta
              name="description"
              content={actionCenterDetails.headlineConfig.secondHeadlineText}
            />
            <meta
              property="og:description"
              content={actionCenterDetails.headlineConfig.secondHeadlineText}
            />
          </>
        )}
      </Head>
      {renderLandingPage(REACT_APP_LANDING_VERSION || actionCenterDetails.version)}
    </>
  );
};

LandingPage.getInitialProps = wrapper.getInitialPageProps(() => async ({
  query,
  req,
  res,
}) => {
  const headers = viewerIpHeaders(req);

  if (res && typeof query?.uuid === 'string' && !query?.uuid?.match(/-/)) {
    const url = await request
      .get<RedirectResponse>(`/public/expand-short-url/${query.uuid}/`, { headers })
      .then(({ data }) => data.url)
      .catch(() => null);

    if (url) {
      res.writeHead(302, { Location: url });
      res.end();
    } else {
      res.statusCode = 404;

      return {};
    }
  }

  const actionCenterDetails = await fetchActionCenterData(query.uuid, req);

  let nonGeocodedTargets = null;

  if (res && !actionCenterDetails) {
    res.statusCode = 404;
  }

  if (actionCenterDetails && !actionCenterDetails.isGeocoded) {
    nonGeocodedTargets = await request
      .get(`/public/act/${query.uuid}/match_targets/`, { headers })
      .then(({ data }) => data)
      .catch(error => null);
  }

  let contactInfo = null;

  if (actionCenterDetails?.token && query.c) {
    contactInfo = await request
      .get(`/public/act/${query.uuid}/contact/?uuid=${query.c}&token=${actionCenterDetails.token}${query.t ? `&type=${query.t}` : ''}`, { headers })
      .then(({ data }) => data)
      .catch(error => null);
  }

  return {
    actionCenterDetails,
    contactInfo,
    nonGeocodedTargets,
  };
});

export default LandingPage;
